import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/clients/trpcClient";
const EMPTY = {
  title: "",
  titleSlug: "",
  subtitle: "",
  description: "",
  isPublished: false
};
function slugify(text) {
  return text.toLowerCase().replace(/å/g, "a").replace(/ä/g, "a").replace(/ö/g, "o").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
function buildMediaPayload(items) {
  return items.map((m, i) => ({
    id: m.id,
    url: m.url,
    altText: m.altText,
    isPrimary: m.isPrimary,
    type: "image",
    displayOrder: i
  }));
}
function toMediaItem(m) {
  return {
    id: m.id,
    url: m.url,
    altText: m.altText ?? "",
    isPrimary: m.isPrimary
  };
}

/**
 * Give just-saved rows their ids without touching anything else: items added,
 * edited or removed while the save was in flight stay as the user left them.
 * Rows are matched by url, in order, so duplicate urls pair up one to one.
 */
function adoptSavedIds(current, saved) {
  const idsByUrl = new Map();
  for (const row of saved) {
    idsByUrl.set(row.url, [...(idsByUrl.get(row.url) ?? []), row.id]);
  }
  return current.map(item => {
    if (item.id !== undefined) return item;
    const id = idsByUrl.get(item.url)?.shift();
    return id === undefined ? item : {
      ...item,
      id
    };
  });
}
export function useExperienceEditor(initial) {
  const $ = _c(57);
  const isNew = !initial?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(initial ?? EMPTY);
  const [autoSlug, setAutoSlug] = useState(isNew);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [mediaItems, setMediaItems] = useState(t0);
  const [mediaStatus, setMediaStatus] = useState(initial?.id ? "loading" : "ready");
  let t1;
  let t2;
  if ($[1] !== initial) {
    t1 = () => {
      setForm(initial ?? EMPTY);
      setAutoSlug(!initial?.id);
      setMediaItems([]);
      if (!initial?.id) {
        setMediaStatus("ready");
        return;
      }
      let stale = false;
      setMediaStatus("loading");
      trpcClient.experiences.media.byExperienceId.query({
        experienceId: initial.id
      }).then(items => {
        if (stale) {
          return;
        }
        setMediaItems(items.map(toMediaItem));
        setMediaStatus("ready");
      }).catch(() => {
        if (!stale) {
          setMediaStatus("error");
        }
      });
      return () => {
        stale = true;
      };
    };
    t2 = [initial];
    $[1] = initial;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== autoSlug) {
    t3 = (field, value) => {
      setForm(prev => {
        const next = {
          ...prev,
          [field]: value
        };
        if (field === "title" && autoSlug) {
          next.titleSlug = slugify(value);
        }
        return next;
      });
    };
    $[4] = autoSlug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const updateField = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setAutoSlug(false);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const disableAutoSlug = t4;
  let t5;
  if ($[7] !== mediaItems || $[8] !== mediaStatus) {
    t5 = async experienceId => {
      if (mediaStatus !== "ready") {
        return;
      }
      const {
        data
      } = await trpcClient.experiences.media.update.mutate({
        experienceId,
        mediaItems: buildMediaPayload(mediaItems)
      });
      setMediaItems(current => adoptSavedIds(current, data));
    };
    $[7] = mediaItems;
    $[8] = mediaStatus;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const saveMedia = t5;
  let t6;
  if ($[10] !== queryClient) {
    t6 = () => queryClient.invalidateQueries({
      queryKey: [["experiences"]]
    });
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const invalidate = t6;
  let t7;
  if ($[12] !== form) {
    t7 = () => trpcClient.experiences.create.mutate({
      title: form.title,
      titleSlug: form.titleSlug,
      subtitle: form.subtitle || undefined,
      description: form.description || undefined,
      isPublished: form.isPublished,
      placeId: form.placeId ?? undefined
    });
    $[12] = form;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== form || $[15] !== invalidate || $[16] !== navigate || $[17] !== saveMedia) {
    t8 = async data_0 => {
      if (data_0.data?.id) {
        await saveMedia(data_0.data.id);
      }
      toast.success("Upplevelse skapad");
      invalidate();
      navigate({
        to: "/upptack/$slug",
        params: {
          slug: form.titleSlug
        }
      });
    };
    $[14] = form;
    $[15] = invalidate;
    $[16] = navigate;
    $[17] = saveMedia;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = {
      mutationFn: t7,
      onSuccess: t8,
      onError: _temp
    };
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const createMutation = useMutation(t9);
  let t10;
  if ($[22] !== form || $[23] !== initial) {
    t10 = () => trpcClient.experiences.update.mutate({
      id: initial.id,
      formData: {
        title: form.title,
        titleSlug: form.titleSlug,
        subtitle: form.subtitle || undefined,
        description: form.description || undefined,
        isPublished: form.isPublished,
        placeId: form.placeId ?? undefined
      }
    });
    $[22] = form;
    $[23] = initial;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== initial || $[26] !== invalidate || $[27] !== saveMedia) {
    t11 = async () => {
      if (initial?.id) {
        await saveMedia(initial.id);
      }
      toast.success("Upplevelse uppdaterad");
      invalidate();
    };
    $[25] = initial;
    $[26] = invalidate;
    $[27] = saveMedia;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = {
      mutationFn: t10,
      onSuccess: t11,
      onError: _temp2
    };
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const updateMutation = useMutation(t12);
  let t13;
  if ($[32] !== initial) {
    t13 = () => trpcClient.experiences.delete.mutate({
      id: initial.id
    });
    $[32] = initial;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== invalidate || $[35] !== navigate) {
    t14 = () => {
      toast.success("Upplevelse borttagen");
      invalidate();
      navigate({
        to: "/upptack"
      });
    };
    $[34] = invalidate;
    $[35] = navigate;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14) {
    t15 = {
      mutationFn: t13,
      onSuccess: t14,
      onError: _temp3
    };
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const deleteMutation = useMutation(t15);
  let t16;
  if ($[40] !== createMutation || $[41] !== form || $[42] !== isNew || $[43] !== updateMutation) {
    t16 = () => {
      if (!form.title.trim()) {
        return toast.error("Titel kr\xE4vs");
      }
      if (!form.titleSlug.trim()) {
        return toast.error("URL-slug kr\xE4vs");
      }
      isNew ? createMutation.mutate() : updateMutation.mutate();
    };
    $[40] = createMutation;
    $[41] = form;
    $[42] = isNew;
    $[43] = updateMutation;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const save = t16;
  let t17;
  if ($[45] !== deleteMutation) {
    t17 = () => {
      if (confirm("\xC4r du s\xE4ker? Detta g\xE5r inte att \xE5ngra.")) {
        deleteMutation.mutate();
      }
    };
    $[45] = deleteMutation;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const remove = t17;
  const t18 = createMutation.isPending || updateMutation.isPending;
  let t19;
  if ($[47] !== deleteMutation.isPending || $[48] !== form || $[49] !== isNew || $[50] !== mediaItems || $[51] !== mediaStatus || $[52] !== remove || $[53] !== save || $[54] !== t18 || $[55] !== updateField) {
    t19 = {
      form,
      updateField,
      disableAutoSlug,
      mediaItems,
      setMediaItems,
      mediaStatus,
      isNew,
      isSaving: t18,
      isDeleting: deleteMutation.isPending,
      save,
      remove
    };
    $[47] = deleteMutation.isPending;
    $[48] = form;
    $[49] = isNew;
    $[50] = mediaItems;
    $[51] = mediaStatus;
    $[52] = remove;
    $[53] = save;
    $[54] = t18;
    $[55] = updateField;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
}
function _temp3(err_1) {
  return toast.error("Kunde inte ta bort", {
    description: err_1.message
  });
}
function _temp2(err_0) {
  return toast.error("Kunde inte uppdatera", {
    description: err_0.message
  });
}
function _temp(err) {
  return toast.error("Kunde inte skapa upplevelse", {
    description: err.message
  });
}